import { Activity, Server, Search, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useHealth, useReadiness, useDashboard, useMe } from "@/api/queries";
import { cn } from "@/lib/utils";

function StatCard({
  icon: Icon,
  label,
  value,
  variant = "default",
}: {
  icon: any;
  label: string;
  value: string | number;
  variant?: "default" | "success" | "warning" | "danger";
}) {
  const cls = {
    default: "text-foreground",
    success: "text-emerald-500",
    warning: "text-amber-500",
    danger: "text-red-500",
  }[variant];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className={cn("h-4 w-4", cls)} />
      </CardHeader>
      <CardContent>
        <div className={cn("text-2xl font-bold", cls)}>{value}</div>
      </CardContent>
    </Card>
  );
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Черновик",
  queued: "В очереди",
  running: "Выполняется",
  completed: "Завершён",
  failed: "Ошибка",
};

export function Dashboard() {
  const { data: health } = useHealth();
  const { data: readiness, isLoading: readinessLoading } = useReadiness();
  const { data: dashboard, isLoading: dashboardLoading } = useDashboard();
  const { data: me } = useMe();

  const allChecks = readiness && Object.values(readiness.checks || {}).every(Boolean);
  const counts = dashboard?.counts;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Главная
        </h1>
        <p className="text-muted-foreground mt-1">
          Здравствуйте, <span className="font-medium text-foreground">{me?.email}</span>
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Activity}
          label="Состояние системы"
          value={
            readinessLoading
              ? "..."
              : allChecks
              ? "OK"
              : readiness?.status === "degraded"
              ? "Деградация"
              : "Неизвестно"
          }
          variant={
            allChecks ? "success" : readiness?.status === "degraded" ? "warning" : "default"
          }
        />
        <StatCard
          icon={Search}
          label="Сборов данных"
          value={dashboardLoading ? "..." : (counts?.investigations?.total ?? 0)}
          variant="default"
        />
        <StatCard
          icon={FileText}
          label="Отчётов"
          value={dashboardLoading ? "..." : (counts?.reports ?? 0)}
          variant="default"
        />
        <StatCard
          icon={Server}
          label="Версия API"
          value={health?.service ? "v" + (health?.version || "0.1.0") : "—"}
          variant="default"
        />
      </div>

      {/* Detailed counts */}
      {counts && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Сборы данных</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Выполняется</span>
                <Badge variant="secondary">{counts.investigations.running}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Завершено</span>
                <Badge className="bg-emerald-500/10 text-emerald-500">
                  {counts.investigations.completed}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ошибки</span>
                <Badge className="bg-red-500/10 text-red-500">{counts.investigations.failed}</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Задачи</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Всего</span>
                <Badge variant="secondary">{counts.jobs.total}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Успешно</span>
                <Badge className="bg-emerald-500/10 text-emerald-500">{counts.jobs.success}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Успешность</span>
                <span className="font-medium">{counts.jobs.success_rate}%</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Ресурсы тенанта</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Пользователей</span>
                <span className="font-medium">{counts.users}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">API-ключей</span>
                <span className="font-medium">{counts.api_keys}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Записей в журнале</span>
                <span className="font-medium">{counts.audit_entries}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Сервисы</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Бэкенд</span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> OK
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Готовность</span>
                <Badge
                  variant="outline"
                  className={
                    allChecks
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-amber-500/10 text-amber-500"
                  }
                >
                  {allChecks ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                  {readiness?.status || "неизвестно"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Среда</span>
                <Badge variant="outline">{health?.env || "?"}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent investigations */}
      {dashboard?.recent_investigations?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Последние сборы данных</CardTitle>
            <CardDescription>5 последних сборов в вашем тенанте</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {dashboard.recent_investigations.map((inv: any) => (
                <div key={inv.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{inv.title}</div>
                    <div className="text-sm text-muted-foreground truncate">
                      Цель: {inv.target}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 shrink-0">
                    <span className="text-sm text-muted-foreground">находок: {inv.findings_count}</span>
                    <Badge
                      variant="outline"
                      className={
                        inv.status === "completed"
                          ? "bg-emerald-500/10 text-emerald-500"
                          : inv.status === "running"
                          ? "bg-blue-500/10 text-blue-500"
                          : "bg-zinc-500/10 text-zinc-400"
                      }
                    >
                      {STATUS_LABELS[inv.status] || inv.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {dashboardLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Загрузка данных…
        </div>
      )}
    </div>
  );
}
